import React from 'react';
import { 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Box, 
  Typography, 
  Chip, 
  CircularProgress, 
  Grow 
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { ProcessingStep } from '../types';

interface ProcessingStepItemProps {
  step: ProcessingStep;
  index: number;
}

export const ProcessingStepItem: React.FC<ProcessingStepItemProps> = ({ step, index }) => {
  const getStepIcon = (step: ProcessingStep) => {
    if (step.status === 'completed') {
      return <CheckCircle sx={{ color: '#10b981', fontSize: '1.25rem' }} />;
    } else if (step.status === 'processing') {
      return <CircularProgress size={18} sx={{ color: 'hsl(var(--accent))' }} />;
    } else {
      return <RadioButtonUnchecked sx={{ color: '#d1d5db', fontSize: '1.25rem' }} />;
    }
  };

  return (
    <Grow
      in={true}
      timeout={300 + index * 100}
      style={{ transformOrigin: '0 0 0' }}
    >
      <ListItem
        sx={{
          py: 1.25,
          px: 1.25,
          borderRadius: 2,
          mb: 1,
          bgcolor: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(148, 163, 184, 0.18)',
          boxShadow: step.status === 'processing' 
            ? '0 0 0 1px rgba(120, 255, 214, 0.12), 0 10px 30px rgba(0, 0, 0, 0.18)' 
            : '0 6px 20px rgba(0, 0, 0, 0.12)',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.22)'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {getStepIcon(step)}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#e5eef8',
                  fontWeight: step.status === 'processing' ? 600 : 400,
                  fontSize: '0.875rem',
                  flex: 1
                }}
              >
                {step.label}
              </Typography>
              {step.status === 'completed' && (
                <Chip
                  label="Done"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(34, 197, 94, 0.14)',
                    color: '#86efac',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 20,
                    border: 'none !important',
                    borderWidth: 0,
                    borderStyle: 'none'
                  }}
                />
              )}
              {step.status === 'processing' && (
                <Chip
                  label="Processing"
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(34, 211, 238, 0.14)',
                    color: '#67e8f9',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: 20,
                    border: 'none !important',
                    borderWidth: 0,
                    borderStyle: 'none'
                  }}
                />
              )}
            </Box>
          }
          secondary={
            step.timestamp && step.status === 'completed' ? (
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'rgba(191, 219, 254, 0.66)', 
                  fontSize: '0.75rem',
                  mt: 0.5,
                  display: 'block'
                }}
              >
                Completed at {step.timestamp}
              </Typography>
            ) : null
          }
        />
      </ListItem>
    </Grow>
  );
}; 